import { comparer, reaction, runInAction } from "mobx";
import { dashboardService } from "../services/dashboard.service";
import { dashboardEChartsStore } from "../stores/e-charts.store";
import { dashboardFiltersStore } from "../stores/filters.store";
import {
  apiGroupBy,
  buildDimensionOptionIds,
  E_CHARTS_MAX_ENTITY_IDS,
  normalizeEntityId,
  normalizeEntityIds,
  otherDimension,
} from "../utils/e-charts-dimensions";
import { buildPickerOptions } from "../utils/hierarchy-picker";
import { resolveSelectedReportingPeriods } from "../utils/reporting-years";

/**
 * One immutable snapshot of everything that decides what a request means.
 *
 * The request body AND the staleness fingerprint are both built from this same
 * object, so a landing response can never be matched against a context its
 * request was not built from. The 400ms refetch debounce leaves a window in
 * which an older response arrives while the ambient state has already moved,
 * which the request sequence alone does not catch (brief 3.2 item 4, extending
 * the load-e-charts-totals.ops.js contextKey pattern to the full context).
 */
export const buildEChartsRequestContext = () => {
  const store = dashboardEChartsStore;
  const years = [...store.activeYears];
  const selection = store.selectionByDimension;

  return {
    displayedDimension: store.displayedDimension,
    selection: {
      bu: { mode: selection.bu.mode, ids: [...selection.bu.ids] },
      country: {
        mode: selection.country.mode,
        ids: [...selection.country.ids],
      },
    },
    drill: store.drillContext
      ? {
          dimension: store.drillContext.dimension,
          entityId: store.drillContext.entityId,
        }
      : null,
    years,
    frameworks: [...(dashboardFiltersStore.filters.frameworks ?? [])],
    reportingPeriods: resolveSelectedReportingPeriods(
      years,
      dashboardFiltersStore.available.reportingYears,
    ),
    // Both vocabularies: an "all" selection materializes from the option list,
    // so the request is not fully determined without them.
    optionIds: buildDimensionOptionIds(dashboardFiltersStore.available.orgTree),
  };
};

/**
 * Which dimension(s) hold an explicit none selection that makes the request
 * unbuildable, as the API-facing name the empty-state copy switches on:
 * "businessUnit", "country", "both", or null. Derived from the SELECTIONS,
 * not from displayedDimension: emptying the OTHER dimension (YY's Countries
 * scenario under View by BU) must be caught too. During a drill the drill
 * target replaces the paused dimension's selection in the request, so only
 * the live dimension's none matters there.
 */
export const noneSelectedDimensionOf = (context) => {
  const displayed = context.displayedDimension;
  const other = otherDimension(displayed);
  const displayedNone = context.selection[displayed].mode === "none";
  const otherNone = !context.drill && context.selection[other].mode === "none";
  if (displayedNone && otherNone) return "both";
  if (displayedNone) return apiGroupBy(displayed);
  if (otherNone) return apiGroupBy(other);
  return null;
};

/**
 * The single request builder for every state: un-narrowed top level, narrowed
 * top level, and drilled.
 *
 * `within` is OMITTED whenever the other dimension is All. All means NO
 * restriction, never the union of the option list: the untagged level_1 roots
 * carry data that a country union would silently drop from every business-unit
 * total (brief 2.2). When drilled, the drill target IS the filter and replaces
 * the paused dimension's selection for the duration of the drill, which is
 * what lets breadcrumb-back restore the prior view with no saved state.
 */
export const buildEChartsRequestBody = (context) => {
  // Dev-time invariant (Brief D R2): a none selection must be refused by the
  // preflight in loadEChartsData BEFORE this builder runs. A none context
  // reaching this point would silently build an All request (displayed) or
  // an empty `within` the API rejects with 400 (other).
  console.assert(
    noneSelectedDimensionOf(context) === null,
    "buildEChartsRequestBody called with a none selection; the preflight must refuse first",
  );
  const displayed = context.displayedDimension;
  const other = otherDimension(displayed);
  const displayedSelection = context.selection[displayed];
  const otherSelection = context.selection[other];

  // Deduped AFTER normalization: distinct picker ids can normalize to the
  // same backend key (country tags differing only in casing), and the API
  // schema rejects duplicate ids outright.
  const uniqueNormalized = (dimension, ids) => [
    ...new Set(normalizeEntityIds(dimension, ids)),
  ];

  const entityIds = uniqueNormalized(
    displayed,
    displayedSelection.mode === "subset"
      ? displayedSelection.ids
      : context.optionIds[displayed],
  );

  let within = null;
  if (context.drill) {
    within = {
      groupBy: apiGroupBy(context.drill.dimension),
      entityIds: [
        normalizeEntityId(context.drill.dimension, context.drill.entityId),
      ],
    };
  } else if (otherSelection.mode === "subset") {
    within = {
      groupBy: apiGroupBy(other),
      entityIds: uniqueNormalized(other, otherSelection.ids),
    };
  }

  return {
    groupBy: apiGroupBy(displayed),
    entityIds,
    years: [...context.years],
    frameworks: [...context.frameworks],
    ...(context.reportingPeriods.length > 0
      ? { reportingPeriods: [...context.reportingPeriods] }
      : {}),
    ...(within ? { within } : {}),
  };
};

export const fingerprintOf = (context) => JSON.stringify(context);

// True when the body would breach the API's hard per-list bound. The panel
// derives the same verdict from the selection counts and renders its own
// explanatory state, so the guard here only has to refuse the doomed request
// (a guaranteed 400) instead of surfacing it as a stale chart.
export const exceedsEntityIdBound = (body) =>
  body.entityIds.length > E_CHARTS_MAX_ENTITY_IDS ||
  (body.within?.entityIds?.length ?? 0) > E_CHARTS_MAX_ENTITY_IDS;

export const loadEChartsData = async () => {
  const store = dashboardEChartsStore;
  const context = buildEChartsRequestContext();

  // PREFLIGHT (Brief D R1/R2): an explicit none selection in EITHER slot
  // means no valid request exists. Refuse BEFORE the body is constructed:
  // a none in the displayed dimension would otherwise enumerate the full
  // option list (an All request the user did not ask for), and a none in
  // the other dimension would build `within.entityIds: []`, a guaranteed
  // 400. Reset like the other refusals; the panel diagnoses the ambient
  // none selection itself, ahead of its loading gates.
  if (noneSelectedDimensionOf(context)) {
    store.nextRequestSeq();
    runInAction(() => {
      store.resetData();
    });
    return;
  }

  const body = buildEChartsRequestBody(context);
  // View context captured with the request, stored beside the rows it
  // produces. While the NEXT refetch is pending the panel renders the
  // previous charts from this snapshot (its own dimension, selection, drill
  // name, years, and BOTH option vocabularies), never from live state. The
  // options are snapshotted because an "all" selection materializes its
  // entity list, names, and colors from them: without the copy, an org-tree
  // reload during the refresh would rewrite the outgoing view's membership
  // ahead of the data swap (this build's Codex finding 1). The fingerprint
  // context is not reused for this because it deliberately excludes
  // display-only facts like names.
  const snapshotOptions = (hierarchy) =>
    buildPickerOptions(dashboardFiltersStore.available.orgTree, hierarchy).map(
      (option) => ({ id: String(option.id), name: option.name }),
    );
  const viewSnapshot = {
    displayedDimension: context.displayedDimension,
    selection: {
      mode: context.selection[context.displayedDimension].mode,
      ids: [...context.selection[context.displayedDimension].ids],
    },
    drillContext: store.drillContext ? { ...store.drillContext } : null,
    years: [...context.years],
    options: {
      bu: snapshotOptions("businessUnit"),
      country: snapshotOptions("country"),
    },
  };

  // Nothing to enumerate yet (the org tree has not arrived). Reset instead of
  // writing an empty payload: written data counts as loaded, and a loaded
  // empty chart renders a misleading no-data explanation during first paint.
  // The reaction watches both option vocabularies, so it refires when the
  // tree lands. The same reset covers the over-the-bound refusal.
  if (body.entityIds.length === 0 || exceedsEntityIdBound(body)) {
    store.nextRequestSeq();
    runInAction(() => {
      store.resetData();
    });
    return;
  }

  const fingerprint = fingerprintOf(context);
  const seq = store.nextRequestSeq();
  runInAction(() => {
    store.setLoadError(false);
    store.setLoading(true);
  });

  let response = null;
  let succeeded = false;
  try {
    response = await dashboardService.getEChartsData(body);
    succeeded = true;
  } catch {
    // Handled below: the failure surfaces as an explicit error state, never
    // as an unhandled rejection or as stale rows presented as current.
  } finally {
    runInAction(() => {
      // A newer request owns the store.
      if (store.isStaleSeq(seq)) return;
      // Same sequence, different context: the ambient state changed inside the
      // debounce window and the pending reaction has already been scheduled
      // against exactly these inputs. Write nothing and leave the spinner up
      // for that refetch rather than flickering it off and straight back on.
      if (fingerprint !== fingerprintOf(buildEChartsRequestContext())) return;
      // The API echoes `within` back on every response to a request that
      // carried it. A response WITHOUT the echo means the serving API did not
      // understand the field and stripped it (validate() runs stripUnknown),
      // which happens when an older API build serves a newer UI during a
      // staggered deploy. Those rows are UNFILTERED; rendering them under
      // filtered headings shows wrong numbers, so it is treated as a failed
      // load instead.
      const withinHonored = !body.within || Boolean(response?.within);
      if (succeeded && withinHonored) {
        store.setData(
          response ?? { rows: [], warnings: [] },
          body,
          fingerprint,
          viewSnapshot,
        );
      } else {
        store.setLoadError(true);
      }
      store.setLoading(false);
    });
  }
};

/**
 * Refetch trigger for the four charts.
 *
 * It watches buildEChartsRequestContext itself rather than a hand-written list
 * of observables, so the watched set and the fingerprinted set cannot drift
 * apart: anything that changes the request refetches, and anything that does
 * not is compared away. That covers both dimensions' selection modes and id
 * lists, the drill, years, frameworks, reporting periods, and BOTH option
 * vocabularies, the last of which is why a default-All first load that
 * short-circuited on an empty option list refetches once the org tree lands
 * (brief 3.2 item 3).
 *
 * Structural comparison is deliberate: a live-selector change during a drill
 * must fire exactly one fetch, and an ambient change that leaves the request
 * identical must fire none.
 */
export const createEChartsFetchReaction = ({ delay = 0 } = {}) =>
  reaction(
    () => buildEChartsRequestContext(),
    () => loadEChartsData(),
    {
      delay,
      fireImmediately: true,
      equals: comparer.structural,
    },
  );
